import { isMobileModeActive } from '../core/deviceMode.js';

/**
 * On-screen controls for touch devices: a virtual joystick (movement) plus
 * dash/ultimate/pause buttons. Mirrors the keyboard bindings 1:1 by feeding
 * InputManager's touch-specific setters (see core/InputManager.js) rather
 * than synthesizing key events — Game.js's update loop then reads both
 * input sources through the exact same getMoveVector/isDashHeld/consume*
 * calls it already used for keyboard-only play.
 *
 * Only mounts when isMobileModeActive(save) is true (see core/deviceMode.js)
 * — hardware auto-detection by default, or the player's explicit Mobile
 * Controls choice (champion-select screen) when they've set one.
 */
export class TouchControls {
  constructor(input, root, { save } = {}) {
    this.input = input;
    this.el = null;
    if (!isMobileModeActive(save)) return;

    this.el = document.createElement('div');
    this.el.className = 'touch-controls';
    this.el.innerHTML = `
      <div class="touch-joystick-zone">
        <div class="touch-joystick-base">
          <div class="touch-joystick-knob"></div>
        </div>
      </div>
      <button type="button" class="touch-button touch-button-pause" data-action="pause" aria-label="Pause">&#10074;&#10074;</button>
      <button type="button" class="touch-button touch-button-ultimate" data-action="ultimate" aria-label="Ultimate">&#9733;</button>
      <button type="button" class="touch-button touch-button-dash" data-action="dash" aria-label="Dash">&#9889;</button>
      <button type="button" class="touch-button touch-button-focus" data-action="focus" aria-label="Focus">&#9678;</button>
    `;
    root.appendChild(this.el);

    this._wireJoystick();
    this._wireButtons();
  }

  _wireJoystick() {
    const zone = this.el.querySelector('.touch-joystick-zone');
    const base = this.el.querySelector('.touch-joystick-base');
    const knob = this.el.querySelector('.touch-joystick-knob');
    const maxDist = 32; // px the knob can travel from center before clamping to full deflection

    let pointerId = null;

    const updateFromEvent = (e) => {
      const rect = base.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      let dx = e.clientX - cx;
      let dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist > maxDist) {
        dx = (dx / dist) * maxDist;
        dy = (dy / dist) * maxDist;
      }
      knob.style.transform = `translate(${dx}px, ${dy}px)`;
      this.input.setTouchMoveVector(dx / maxDist, dy / maxDist);
    };

    const release = (e) => {
      if (e.pointerId !== pointerId) return;
      pointerId = null;
      knob.style.transform = 'translate(0px, 0px)';
      this.input.setTouchMoveVector(0, 0);
    };

    zone.addEventListener('pointerdown', (e) => {
      if (pointerId !== null) return;
      pointerId = e.pointerId;
      zone.setPointerCapture(pointerId);
      updateFromEvent(e);
      e.preventDefault();
    });
    zone.addEventListener('pointermove', (e) => {
      if (e.pointerId !== pointerId) return;
      updateFromEvent(e);
      e.preventDefault();
    });
    zone.addEventListener('pointerup', release);
    zone.addEventListener('pointercancel', release);
  }

  _wireButtons() {
    const dash = this.el.querySelector('.touch-button-dash');
    dash.addEventListener('pointerdown', (e) => {
      dash.setPointerCapture(e.pointerId);
      this.input.setTouchDashHeld(true);
      e.preventDefault();
    });
    const releaseDash = () => this.input.setTouchDashHeld(false);
    dash.addEventListener('pointerup', releaseDash);
    dash.addEventListener('pointercancel', releaseDash);

    // Focus (§D4) — held, like dash, so it captures the pointer the same way:
    // without capture, sliding a thumb off the button never fires pointerup
    // and the player walks at 45% speed for the rest of the run.
    const focus = this.el.querySelector('.touch-button-focus');
    focus.addEventListener('pointerdown', (e) => {
      focus.setPointerCapture(e.pointerId);
      this.input.setTouchFocusHeld(true);
      e.preventDefault();
    });
    const releaseFocus = () => this.input.setTouchFocusHeld(false);
    focus.addEventListener('pointerup', releaseFocus);
    focus.addEventListener('pointercancel', releaseFocus);

    // Always mounted: every champion has an ultimate, with no perk unlock in
    // front of it.
    const ultimate = this.el.querySelector('.touch-button-ultimate');
    ultimate.addEventListener('pointerdown', (e) => {
      this.input.queueUltimatePressed();
      e.preventDefault();
    });

    const pause = this.el.querySelector('.touch-button-pause');
    pause.addEventListener('pointerdown', (e) => {
      this.input.queuePausePressed();
      e.preventDefault();
    });
  }

  destroy() {
    this.input.setTouchMoveVector(0, 0);
    this.input.setTouchDashHeld(false);
    this.input.setTouchFocusHeld(false);
    this.el?.remove();
    this.el = null;
  }
}
